import frontendUtils from 'honda_cars_ecux/utils/frontendUtils';

/**
 * Sub-controller for Test Drive Flyout — Confirm Honda Centre.
 *
 * Owns a flow-local centre search and selection for the lead payload only. Searches write
 * `tempuserloc` and fetch dealers with an elevated limit; they must not write `userloc` or
 * dispatch `amb:showroom-locator:showroom-changed`.
 */
export const testDriveHondaCentre = {
  /**
   * Bootstrap the Confirm Honda Centre view. Idempotent; safe to call once at boot.
   */
  testDriveHondaCentreView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * Dealer fetch page size for this view only.
     *
     * @private
     * @readonly
     */
    DEALERS_LIMIT: 20,

    /**
     * Centres rendered immediately after fetch. The rest wait for a scroll-to-end reveal.
     *
     * @private
     * @readonly
     */
    INITIAL_DEALERS_COUNT: 10,

    /**
     * Simulated ajax pause before appending remaining centres.
     *
     * @private
     * @readonly
     */
    REVEAL_DELAY_MS: 750,

    /**
     * Cookie used for searches in this view — never `userloc`.
     *
     * @private
     * @readonly
     */
    LOCATION_COOKIE_ID: 'tempuserloc',

    /**
     * Radio `name` rewritten onto injected list markup so this view does not share a radio group
     * with showroom locator / finance / legacy containers.
     *
     * @private
     * @readonly
     */
    DEALER_INPUT_NAME: 'test-drive-location-dealers',

    /**
     * @private
     *
     * @type {boolean}
     */
    isWired: false,

    /**
     * @private
     *
     * @type {boolean}
     */
    isViewActive: false,

    /**
     * Store nodes fetched but not yet painted. Emptied after the scroll reveal.
     *
     * @private
     *
     * @type {Element[]}
     */
    pendingDealerNodes: [],

    /**
     * @private
     *
     * @type {number|null}
     */
    revealTimeoutId: null,

    /**
     * @private
     *
     * @type {IntersectionObserver|null}
     */
    revealObserver: null,

    /**
     * Scroll fallback when IntersectionObserver is missing.
     *
     * @private
     *
     * @type {Function|null}
     */
    revealScrollHandler: null,

    /**
     * @private
     *
     * @type {boolean}
     */
    isRevealing: false,

    /**
     * @private
     *
     * @type {{
     *   view: HTMLElement | null,
     *   modalBody: HTMLElement | null,
     *   locationInput: HTMLInputElement | null,
     *   clearBtn: HTMLElement | null,
     *   dealersList: HTMLElement | null,
     *   nextBtn: HTMLButtonElement | null,
     *   selection: HTMLElement | null
     * }}
     */
    elements: {
      view: null,
      modalBody: null,
      locationInput: null,
      clearBtn: null,
      dealersList: null,
      nextBtn: null,
      selection: null
    },

    /**
     * Lazy-wire on first show; re-enter refreshes the prefilled location and list when needed.
     */
    bootstrap() {
      document.body.addEventListener('amb:modal-view-changed', event => {
        if (event.detail?.view === 'test-drive-honda-centre') {
          this.isViewActive = true;
          this.wire();
          this.onEnter();

          return;
        }

        this.isViewActive = false;
      });

      $('body').on('location:afterUpdate', (event, data) => this.onLocationAfterUpdate(event, data));
    },

    /**
     * Resolve element refs and attach listeners once.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        return true;
      }

      const view = document.querySelector('.configurator-modal .modal-view[view="test-drive-honda-centre"]');

      if (!view) {
        return false;
      }

      const locationInput = view.querySelector('#test-drive-honda-centre-autocomplete');
      const dealersList = view.querySelector('#test-drive-honda-centre-dealers');
      const nextBtn = view.querySelector('.test-drive-honda-centre__next');

      if (!locationInput || !dealersList || !nextBtn) {
        return false;
      }

      this.elements.view = view;
      this.elements.modalBody = view.querySelector('.modal-body');
      this.elements.locationInput = locationInput;
      this.elements.clearBtn = view.querySelector('.js-delete-location');
      this.elements.dealersList = dealersList;
      this.elements.nextBtn = nextBtn;
      this.elements.selection = view.querySelector('.test-drive-honda-centre__selection');

      locationInput.addEventListener('input', () => this.onLocationInput());
      this.elements.clearBtn?.addEventListener('click', event => this.onClearLocation(event));
      view.addEventListener('change', event => this.onDealerSelectionChange(event));

      this.isWired = true;

      return true;
    },

    /**
     * Prefill from `userloc` when the field is empty. Keep an existing search and selection —
     * going back to Choose Models and returning must not refetch, same as Back from Shopper Details.
     *
     * @private
     */
    onEnter() {
      const hasDealers = !!this.elements.dealersList?.querySelector(this.dealerInputSelector());

      this.prefillFromUserLoc(false);

      if (!hasDealers) {
        this.seedTempUserLocFromUserLoc();
        this.fetchDealers();
      }

      this.syncClearVisibility();
      this.syncSelectionFromChecked();
      this.syncNextDisabled();
    },

    /**
     * React to Places writes into `tempuserloc` while this view is active.
     *
     * @param {JQuery.Event} event
     * @param {{ cookieID?: string }} data
     *
     * @private
     */
    onLocationAfterUpdate(event, data) {
      if (!this.isViewActive || !this.isWired) {
        return;
      }

      if ((data && data.cookieID) !== this.LOCATION_COOKIE_ID) {
        return;
      }

      const location = this.readCookie(this.LOCATION_COOKIE_ID);

      if (location?.summary && this.elements.locationInput) {
        this.elements.locationInput.value = location.summary;
        this.syncClearVisibility();
      }

      this.fetchDealers();
    },

    /**
     * Prefill the postcode field from the shopper's global location without mutating it.
     *
     * @param {boolean} [force=false] When true, overwrite whatever is currently in the input.
     *
     * @private
     */
    prefillFromUserLoc(force) {
      const userLocation = this.readCookie('userloc');
      const input = this.elements.locationInput;

      if (!input || !userLocation?.summary) {
        return;
      }

      if (force || !input.value.trim()) {
        input.value = userLocation.summary;
      }
    },

    /**
     * Copy `userloc` into `tempuserloc` so the dealer fetch can read coordinates without joining
     * the shared `stores:getDealers` fan-out.
     *
     * @private
     */
    seedTempUserLocFromUserLoc() {
      const userLocation = this.readCookie('userloc');

      if (!userLocation?.lat || !userLocation?.lng) {
        return;
      }

      frontendUtils.setCookie(this.LOCATION_COOKIE_ID, JSON.stringify(userLocation), 15);
    },

    /**
     * Own dealer fetch — only caller that passes `limit=20`. Does not trigger `stores:getDealers`.
     *
     * @private
     */
    fetchDealers() {
      const dealersList = this.elements.dealersList;
      const action = dealersList?.dataset.action;

      if (!dealersList || !action) {
        return;
      }

      this.resetReveal();

      const location = this.readCookie(this.LOCATION_COOKIE_ID) || this.readCookie('userloc');

      if (!location?.lat || !location?.lng) {
        dealersList.innerHTML = '';
        this.clearSelectionFields();
        this.syncNextDisabled();

        return;
      }

      if (!this.readCookie(this.LOCATION_COOKIE_ID)?.lat) {
        this.seedTempUserLocFromUserLoc();
      }

      this.clearSelectionFields();
      this.syncNextDisabled();

      dealersList.innerHTML = '<div class="skeleton skeleton-lg my-2"></div>' +
        '<div class="configurator-location__dealers__list">' +
        '<div class="skeleton skeleton-sm mb-2"></div>' +
        '<div class="skeleton skeleton-md mb-2"></div>' +
        '<hr class="m-2" />' +
        '<div class="skeleton skeleton-sm mb-2"></div>' +
        '<div class="skeleton skeleton-md mb-2"></div>' +
        '<hr class="m-2" />' +
        '<div class="skeleton skeleton-sm mb-2"></div>' +
        '<div class="skeleton skeleton-md mb-2"></div>' +
        '</div>';

      $.ajax({
        url: action,
        type: 'get',
        dataType: 'json',
        data: {
          userLocationCookieID: this.LOCATION_COOKIE_ID,
          limit: this.DEALERS_LIMIT,
          requestFrom: 'Test Drive Honda Centre'
        },
        success: data => this.onDealersFetched(data),
        error: () => this.onDealersFetchFailed()
      });
    },

    /**
     * @param {{ innerHTML?: string }} data
     *
     * @private
     */
    onDealersFetched(data) {
      const dealersList = this.elements.dealersList;
      let html = data?.innerHTML || '';

      if (!dealersList) {
        return;
      }

      html = html
        .replace(/\s(id|for)="([^"]*)"/g, (match, attr, value) => ' ' + attr + '="' + value + '-td"')
        .replace(/name="user-location-dealers"/g, 'name="' + this.DEALER_INPUT_NAME + '"');

      this.resetReveal();

      const parsed = this.parseDealerMarkup(html);

      if (!parsed) {
        dealersList.innerHTML = html;
        this.syncSelectionFromChecked();
        this.syncNextDisabled();

        return;
      }

      const initialStores = parsed.stores.slice(0, this.INITIAL_DEALERS_COUNT);
      const list = document.createElement('div');

      this.pendingDealerNodes = parsed.stores.slice(this.INITIAL_DEALERS_COUNT);
      list.className = parsed.listClassName;

      initialStores.forEach(store => list.appendChild(store));

      dealersList.innerHTML = '';

      if (parsed.title) {
        dealersList.appendChild(parsed.title);
      }

      dealersList.appendChild(list);
      this.syncSelectionFromChecked();
      this.syncNextDisabled();

      if (this.pendingDealerNodes.length) {
        this.watchDealerReveal(list);
      }
    },

    /**
     * Split the `Stores-GetDealers` fragment into title + store cards. Returns null when the
     * markup has no cards (empty state copy).
     *
     * @param {string} html
     *
     * @returns {{ stores: Element[], title: Element|null, listClassName: string }|null}
     *
     * @private
     */
    parseDealerMarkup(html) {
      const template = document.createElement('template');

      template.innerHTML = html.trim();

      const stores = Array.from(
        template.content.querySelectorAll('.configurator-location__dealers__list__store')
      );

      if (!stores.length) {
        return null;
      }

      const listEl = template.content.querySelector('.configurator-location__dealers__list');

      return {
        stores: stores,
        title: template.content.querySelector('.configurator-location__dealers__title'),
        listClassName: listEl ? listEl.className : 'configurator-location__dealers__list pt-1'
      };
    },

    /**
     * Reveal remaining cards when the shopper reaches the end of the painted list.
     *
     * @param {HTMLElement} list
     *
     * @private
     */
    watchDealerReveal(list) {
      const sentinel = document.createElement('div');

      sentinel.className = 'test-drive-honda-centre__dealers-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      list.appendChild(sentinel);

      if (typeof IntersectionObserver === 'function') {
        this.revealObserver = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) {
            return;
          }

          this.disconnectRevealObserver();
          this.revealRemainingDealers(list, sentinel);
        }, {
          root: this.getDealersScroller(),
          threshold: 0
        });

        this.revealObserver.observe(sentinel);

        return;
      }

      this.revealScrollHandler = () => {
        const scroller = this.getDealersScroller();

        if (!scroller || scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 8) {
          return;
        }

        scroller.removeEventListener('scroll', this.revealScrollHandler);
        this.revealScrollHandler = null;
        this.revealRemainingDealers(list, sentinel);
      };

      const scroller = this.getDealersScroller();

      scroller?.addEventListener('scroll', this.revealScrollHandler);
      this.revealScrollHandler();
    },

    /**
     * Show a bottom skeleton, wait {@link REVEAL_DELAY_MS}, then append the remaining cards.
     *
     * @param {HTMLElement} list
     * @param {HTMLElement} sentinel
     *
     * @private
     */
    revealRemainingDealers(list, sentinel) {
      if (this.isRevealing || !this.pendingDealerNodes.length) {
        return;
      }

      this.isRevealing = true;
      sentinel.className = 'test-drive-honda-centre__dealers-loading';
      sentinel.innerHTML = '<div class="skeleton skeleton-sm"></div>' +
        '<div class="skeleton skeleton-md"></div>';

      this.revealTimeoutId = window.setTimeout(() => {
        this.revealTimeoutId = null;
        this.isRevealing = false;

        if (!list.isConnected || !this.elements.dealersList?.contains(list)) {
          this.pendingDealerNodes = [];

          return;
        }

        sentinel.remove();
        this.pendingDealerNodes.forEach(node => list.appendChild(node));
        this.pendingDealerNodes = [];
        this.syncSelectionFromChecked();
        this.syncNextDisabled();
      }, this.REVEAL_DELAY_MS);
    },

    /**
     * @private
     */
    disconnectRevealObserver() {
      if (!this.revealObserver) {
        return;
      }

      this.revealObserver.disconnect();
      this.revealObserver = null;
    },

    /**
     * Drop an in-flight reveal so a new fetch or a cleared list cannot append stale cards.
     *
     * @private
     */
    resetReveal() {
      if (this.revealTimeoutId) {
        window.clearTimeout(this.revealTimeoutId);
        this.revealTimeoutId = null;
      }

      this.disconnectRevealObserver();

      if (this.revealScrollHandler) {
        this.getDealersScroller()?.removeEventListener('scroll', this.revealScrollHandler);
      }

      this.revealScrollHandler = null;
      this.pendingDealerNodes = [];
      this.isRevealing = false;
    },

    /**
     * Drop the in-flight skeleton so a failed request does not leave Next stuck behind a loading UI.
     *
     * @private
     */
    onDealersFetchFailed() {
      this.resetReveal();

      if (this.elements.dealersList) {
        this.elements.dealersList.innerHTML = '';
      }

      this.clearSelectionFields();
      this.syncNextDisabled();
    },

    /**
     * @private
     */
    onLocationInput() {
      this.syncClearVisibility();

      if (!this.elements.locationInput?.value.trim()) {
        this.resetReveal();
        this.elements.dealersList.innerHTML = '';
        this.clearSelectionFields();
        this.syncNextDisabled();
      }
    },

    /**
     * Clear the location field and dealer list without touching `userloc`.
     *
     * @param {MouseEvent} event
     *
     * @private
     */
    onClearLocation(event) {
      event.preventDefault();

      if (this.elements.locationInput) {
        this.elements.locationInput.value = '';
        this.elements.locationInput.focus();
      }

      if (this.elements.dealersList) {
        this.resetReveal();
        this.elements.dealersList.innerHTML = '';
      }

      this.clearSelectionFields();
      this.syncClearVisibility();
      this.syncNextDisabled();
    },

    /**
     * Radio `change` — keep hidden lead fields and Next in sync with the checked centre.
     *
     * @param {Event} event
     *
     * @private
     */
    onDealerSelectionChange(event) {
      if (!event.target.matches(this.dealerInputSelector())) {
        return;
      }

      this.syncSelectionFromChecked();
      this.syncNextDisabled();
    },

    /**
     * @private
     */
    syncSelectionFromChecked() {
      const selected = this.elements.dealersList?.querySelector(this.dealerInputSelector(':checked'));

      if (!selected) {
        this.clearSelectionFields();

        return;
      }

      const selection = this.elements.selection;
      const postcodeInput = selection?.querySelector('input[name="AddressPostcode"]');
      const dealerIdInput = selection?.querySelector('input[name="DealerId"]');
      const expertIdInput = selection?.querySelector('input[name="ExpertId"]');

      if (postcodeInput) {
        postcodeInput.value = selected.dataset.storepostcode || '';
      }

      if (dealerIdInput) {
        dealerIdInput.value = selected.dataset.dealerid || selected.value || '';
      }

      if (expertIdInput) {
        expertIdInput.value = selected.dataset.expertid || '';
      }
    },

    /**
     * @private
     */
    clearSelectionFields() {
      const selection = this.elements.selection;

      selection?.querySelectorAll('input').forEach(input => {
        input.value = '';
      });
    },

    /**
     * @private
     */
    syncNextDisabled() {
      const selected = this.elements.dealersList?.querySelector(this.dealerInputSelector(':checked'));
      const hasExpert = !!(selected && selected.dataset.expertid);
      const hasPostcode = !!(selected && selected.dataset.storepostcode);

      this.elements.nextBtn?.toggleAttribute('disabled', !(hasExpert && hasPostcode));
    },

    /**
     * @private
     */
    syncClearVisibility() {
      const hasValue = !!(this.elements.locationInput && this.elements.locationInput.value.trim());

      if (this.elements.clearBtn) {
        this.elements.clearBtn.style.display = hasValue ? 'inline-flex' : 'none';
      }
    },

    /**
     * Scroll root for progressive reveal — the view `.modal-body`, matching showroom locator.
     *
     * @returns {HTMLElement|null}
     *
     * @private
     */
    getDealersScroller() {
      return this.elements.modalBody || this.elements.dealersList;
    },

    /**
     * @param {string} [suffix='']
     *
     * @returns {string}
     *
     * @private
     */
    dealerInputSelector(suffix) {
      return 'input[name="' + this.DEALER_INPUT_NAME + '"]' + (suffix || '');
    },

    /**
     * @param {string} cookieID
     *
     * @returns {Object|null}
     *
     * @private
     */
    readCookie(cookieID) {
      try {
        return JSON.parse(frontendUtils.getCookie(cookieID) || 'null');
      } catch (error) {
        return null;
      }
    }
  }
};
